import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import InputLabel from '../shared/atoms/InputLabel';
import Button from '../shared/Button';
import { trpc } from '@/utils/trpc';
import useNotification from '@/hooks/useNotification';
import { useRouter } from 'next/router';
import { NoteState } from '../layout/StandardPhotoGrid';
import { LINK_ROW_SPAN } from '@/pages/collection/[id]';
import XButton from '../shared/atoms/XButton';

interface AddNoteModalProps {
  bookmarkId: number;
  closeSelf: () => void;
  setNoteState: (value: NoteState) => void;
  noteState: NoteState;
}

export default function AddNoteModal({
  bookmarkId,
  closeSelf,
  setNoteState,
  noteState,
}: AddNoteModalProps) {
  const router = useRouter();
  const collectionUuid = String(router.query.id);
  const editBookmarkNoteMutation = trpc.collections.editBookmarkNote.useMutation();
  const { data: bookmark, refetch } = trpc.collections.getBookmarkById.useQuery(bookmarkId, {
    enabled: bookmarkId > 0,
    refetchOnWindowFocus: false,
  });
  const { showNotification } = useNotification();
  const [note, setNote] = useState(bookmark?.bookmark?.note ?? '');
  const { refetch: refetchCollectionData } = trpc.collections.getUserCollectionById.useQuery(
    collectionUuid,
    { enabled: !!collectionUuid }
  );
  const [saveLoading, setSaveLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  useEffect(() => {
    setNote(bookmark?.bookmark?.note ?? '');
  }, [bookmark]);

  const handleNoteChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNote(e.target.value);
  }, []);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    try {
      setSaveLoading(true);
      await editBookmarkNoteMutation.mutateAsync({
        id: bookmarkId,
        note: note,
      });
      refetchCollectionData();
      showNotification({ message: 'Note updated' });
      refetch();
      closeSelf();
    } catch (e) {
      showNotification({ message: `Something went wrong: ${e.message}` }, 6000);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleRemoveClick = async () => {
    try {
      setRemoveLoading(true);
      await editBookmarkNoteMutation.mutateAsync({
        id: bookmarkId,
        note: '',
      });
      setNoteState({ ...noteState, [bookmarkId]: { span: LINK_ROW_SPAN, showNote: false } });
      refetchCollectionData();
      showNotification({ message: 'Note removed' });
      refetch();
      closeSelf();
    } catch (e) {
      showNotification({ message: `Something went wrong: ${e.message}` }, 6000);
    } finally {
      setRemoveLoading(false);
    }
  };

  return (
    <div className="border-2 border-white p-[32px] w-[475px]">
      <XButton onClose={closeSelf} />
      <form onSubmit={handleSubmit} className="h-full flex flex-col justify-between gap-[24px]">
        <div className="grid gap-[8px]">
          <InputLabel>Note</InputLabel>
          <textarea
            name="note"
            value={note}
            onChange={handleNoteChange}
            placeholder="Why did you save this?"
            className="bg-black px-[16px] py-[9px] border border-white border-solid focus:outline-none focus:border-white focus:ring-0 font-nhg tracking-[0.48px] placeholder:text-white/40"
            rows={8}
            maxLength={300}
            autoFocus
          />
          <span className="text-[12px] leading-[16.8px] tracking-[0.36px]">
            ({note.length}/300 Characters)
          </span>
        </div>
        <div className="grid gap-[15px] mt-[8px]">
          <div className="grid gap-[10px] grid-cols-[1fr_1fr]">
            {bookmark?.bookmark?.note && bookmark.bookmark.note.length > 0 ? (
              <>
                <Button
                  type="secondary"
                  disabled={editBookmarkNoteMutation.isLoading}
                  className="place-self-start"
                  onClick={handleRemoveClick}
                  loading={removeLoading}
                >
                  REMOVE
                </Button>
                <Button
                  type="primary"
                  buttonType="submit"
                  disabled={note.length <= 0 || editBookmarkNoteMutation.isLoading}
                  className="place-self-end"
                  loading={saveLoading}
                >
                  SAVE
                </Button>
              </>
            ) : (
              <div className="col-span-2 flex justify-center">
                <Button
                  type="primary"
                  buttonType="submit"
                  disabled={note.length <= 0 || editBookmarkNoteMutation.isLoading}
                  loading={saveLoading}
                >
                  ADD
                </Button>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
